// 二级单「权益明细」表格形态重构版（ProTablePlusForm + custom-component）
// 容器：ProTablePlusForm（受控 value + onChange），每行 = 一个权益分组
// 列结构：
//   ① 权益名称 → custom-component（BenefitNameCell）：开启开关 + 必填星号 + 名称
//                （required 分组 disabled=true 恒开；view 只读态渲染禁用开关）
//   ③ 权益说明 → custom-component（BenefitDescCell，整列一个 custom-component）：
//                按 record.enabled 决定是否渲染 attr 字段（关闭时不渲染）
//                编辑态 = 行内子表单：每个 attr 走 DescriptionsFormItem 的 SFormItem 分支
//                （rules 复用 attrToSFormProps），必填报错由 SFormItem 展示在各字段下方
// 解析 schema 逻辑全部复用 BenefitConfig/config.ts：
//   - attrToSFormProps: attribute → 控件配置（elementProps/options/disabled/valueFormat + rules）
//   - buildRowsFromBenefits: 详情回填（统一由父层 setFieldsValue 注入，本组件不做内部回填）
// 校验：列级 rule 已移除，下沉到各 cell 子表单 —— validateFields 遍历 cellFormMap 触发每行子表单校验，
//   错误定位到具体 attr 字段下方；reject 聚合各字段错误消息字符串（父表单 watchChildFormValidate 取 message）
// 提交逻辑：
//   - 父组件把 value（RowData[] 全量权益列表）原样持久化到 formData.benefit_rows，提交时经 config.ts rowsToBenefits 直转（无扁平中间态，行内自含 template）
//   - 当前组件不直接组装提交数据，保持与 BenefitConfig 一致的提交流程
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Button, Checkbox, Form, Table } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem';
import { ProTablePlusForm } from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import type { IpBenefitValueDto } from '#services/api';
import {
  ActionCell,
  getOpinionTitle,
  OpinionCell,
  createRenderBatchEditTitle,
  type NegotiationEditState,
  type NegotiationItem,
} from '../NegotiationTable/renderCells';
import {
  attrToSFormProps,
  clearNotFillableRows,
  enableBenefitRows,
  isBenefitGroupRequired,
  serializeBenefitTemplate,
  type BenefitAttrDef,
  type BenefitGroupDef,
  type BenefitRowData,
} from './config';
import RoundDiffCell, {
  DIFF_TEXT_CLASS,
  OLD_VALUE_CLASS,
} from '../RoundDiff/RoundDiffCell';
import type { BenefitDiffMap, RoundDiffValue } from '../RoundDiff/types';
import { AddBenefitModal } from './AddBenefitModal';
import './index.less';

/** 权益谈判配置（外包场景追加「回复/意见反馈」列；权益内容仍按 view 只读，谈判列独立于 view 控制） */
export interface BenefitConfigNewNegotiation {
  /** 谈判条款（negotiation_items，按 item_key=benefit_code 回显 action/opinion） */
  items?: NegotiationItem[];
  /** 编辑状态（key=item_key=benefit_code） */
  editState?: Record<string, NegotiationEditState>;
  /** 是否可谈判（当前轮次 + 版权方待确认） */
  editable?: boolean;
  /** 场景模式：external=外包谈判（回复/意见可编辑），internal=内包商务修改（回复/意见只读展示版权方回复） */
  mode?: 'internal' | 'external';
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（Popover 选操作后整批应用；itemKeys 限定本表格权益 code） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
}

interface BenefitConfigNewProps {
  /** 权益分组定义（useDemandOptions('ip_cooperation_benefit').options） */
  options?: BenefitGroupDef[];
  /** 受控 value：每行 = 一个权益分组 */
  value?: BenefitRowData[];
  /** value 变化回调（父组件持久化到 form state 用于提交） */
  onChange?: (rows: BenefitRowData[]) => void;
  /** 只读态 */
  view?: boolean;
  /** 父级 formData（条件联动求值兜底，如 products 联动） */
  formData?: Record<string, any>;
  /** 谈判配置（传了才追加「回复/意见反馈」列，权益内容仍按 view 只读；不传保持纯权益展示） */
  negotiation?: BenefitConfigNewNegotiation;
  /** offer 谈判模式：编辑态隐藏未开启行/checkbox，追加删除操作列 + summary 新增权益（仅可选行可删/可加） */
  mode?: 'offer-negotiation';
  /** 历史轮次对比 diff（key=group_value → 行级类型 + attr 级 diff）；传了则 attr 只读渲染走 RoundDiffCell，不传保持原展示 */
  diffData?: BenefitDiffMap;
  /** 对比态开关：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；SecondaryDetailContent 传 compareActive */
  compareEnabled?: boolean;
}

/** 父表单 watchChildFormValidate 在提交时调用的校验能力（委托内部 ProTablePlusForm validateFields） */
export interface BenefitConfigNewRef {
  validateFields: () => Promise<unknown>;
}

/**
 * 从 options 构造初始 value：required=true 默认 enabled=true，required=false 默认 false
 * template 一并序列化回填（rowsToBenefits 提交直取行 template，避免新建场景缺 version/group）
 */
function buildInitialValue(options: BenefitGroupDef[]): BenefitRowData[] {
  return options.map((g) => ({
    group_value: g.code,
    group_name: g.name,
    enabled: isBenefitGroupRequired(g),
    required: isBenefitGroupRequired(g),
    attributes: g.attributes,
    template: serializeBenefitTemplate(g),
  }));
}

/** 只读态权益属性值展示（对齐只读详情纯展示范式） */
function renderBenefitAttrViewValue(
  record: BenefitRowData,
  attr: BenefitAttrDef
) {
  const v = record?.[attr.attr_code];
  if (v === undefined || v === null)
    return <span className="text-color-3">--</span>;
  if (Array.isArray(v)) {
    const text = v.map(String).filter(Boolean).join('/');
    return text || '--';
  }
  return String(v);
}

/**
 * 历史轮次对比态权益属性值渲染：行级 added/deleted 整行着色；attr 级 diff 走 RoundDiffCell（最新值着色 + 历史值删除线）。
 * 旧值渲染复用 record 克隆（把 attr_code 替换为 oldValue），与只读态展示格式一致。
 */
function renderBenefitAttrDiff(
  record: BenefitRowData,
  attr: BenefitAttrDef,
  diffData?: BenefitDiffMap
) {
  const rowDiff = diffData?.[record?.group_value ?? ''];
  const attrDiff = rowDiff?.attrs?.[attr.attr_code] as
    | RoundDiffValue
    | undefined;
  const renderCurrent = () => renderBenefitAttrViewValue(record, attr);
  if (rowDiff?.rowType === 'added')
    return <span className={DIFF_TEXT_CLASS.added}>{renderCurrent()}</span>;
  if (rowDiff?.rowType === 'deleted')
    return <span className={OLD_VALUE_CLASS}>{renderCurrent()}</span>;
  if (attrDiff) {
    return (
      <RoundDiffCell
        diff={attrDiff}
        renderNewValue={renderCurrent}
        renderOldValue={(oldValue: unknown) =>
          renderBenefitAttrViewValue(
            { ...record, [attr.attr_code]: oldValue },
            attr
          )
        }
      />
    );
  }
  return renderCurrent();
}

/** 行级更新回调：整行 patch 写回（ProTablePlusForm 受控 rows 范式） */
type RowChange = (row: BenefitRowData, patch: Partial<BenefitRowData>) => void;

/** 单行「权益说明」cell 子表单暴露的校验能力（外层 validateFields 遍历收集） */
interface BenefitDescCellRef {
  validateFields: () => Promise<unknown>;
}

/** cell 子表单实例注册回调（rowKey=group_value；api 为 null 表示卸载注销） */
type RegisterCell = (rowKey: string, api: BenefitDescCellRef | null) => void;

/** BenefitDescCell props（编辑态子表单；只读态纯文本展示） */
interface BenefitDescCellProps {
  editable: boolean;
  row: BenefitRowData;
  onRowChange: RowChange;
  formValues: Record<string, any>;
  registerCell: RegisterCell;
  /** 历史轮次对比态：attr 只读渲染改走 RoundDiffCell */
  diffData?: BenefitDiffMap;
}

/**
 * 权益名称列 customComp：required 分组显示 `*`+名称（恒开，无开关）；可选分组显示 checkbox(enabled)+名称。
 * 勾选/取消勾选 → onRowChange 写回 enabled（驱动整行重渲染，联动「权益说明」列显示/隐藏 attr）
 */
function BenefitNameCell({
  editable,
  row,
  onRowChange,
  hideCheckbox,
  diffData,
}: {
  editable: boolean;
  row: BenefitRowData;
  onRowChange: RowChange;
  /** offer 谈判模式下隐藏勾选开关（增删改走操作列 + summary） */
  hideCheckbox?: boolean;
  /** 历史轮次对比态：行级 added/deleted 着色权益名称 */
  diffData?: BenefitDiffMap;
}) {
  // 行级对比态着色：新增整行绿 / 删除整行灰删除线（对齐设计稿图例）
  const rowType = diffData?.[row?.group_value ?? '']?.rowType;
  return (
    <div className="flex items-center gap-2">
      {!row?.required && !hideCheckbox && (
        <Checkbox
          className={'mr-0'}
          size="small"
          checked={!!row?.enabled}
          disabled={!editable}
          onChange={(e: any) =>
            onRowChange(row, { enabled: !!e?.target?.checked })
          }
        />
      )}
      {row?.required && editable && <span className="text-[#f5222d]">*</span>}
      <span
        className={
          rowType === 'added'
            ? DIFF_TEXT_CLASS.added
            : rowType === 'deleted'
              ? OLD_VALUE_CLASS
              : undefined
        }
      >
        {row?.group_name}
      </span>
    </div>
  );
}

/**
 * 权益说明列 customComp（整列一个 custom-component）：
 * 编辑态 = 行内子表单：每个 attr 走 DescriptionsFormItem 的 SFormItem 分支（rules 复用 attrToSFormProps），
 *   必填报错由 SFormItem 展示在各字段下方（不再只显示在 cell 底部）；
 *   受控值双向同步：row → useEffect setFieldsValue 灌入 form；编辑 → Form onValuesChange → onRowChange 写回 row
 *   （rc-field-form 同值 setFieldsValue 不触发 onValuesChange，无循环）
 * 校验：cell 挂载时经 registerCell 把子表单实例注册到外层 cellFormMap，提交时外层 validateFields 遍历触发。
 *   注意：ProTable cell 渲染链（EditWrapper→cellEditMemo）是 memo 非 forwardRef，会丢弃 ref，
 *   故必须经 props 显式传 registerCell 注册，不能依赖 ref。
 * 只读态复用 renderBenefitAttrViewValue 纯文本；未开启行（可选未勾选）返回 null。
 */
function BenefitDescCell({
  editable,
  row,
  onRowChange,
  formValues,
  registerCell,
  diffData,
}: BenefitDescCellProps) {
  const [form] = Form.useForm();
  const enabled = row?.required ? true : !!row?.enabled;
  // diff 对比态：被删除权益行（历史轮有/最新轮未开启）enabled=false 但需展示历史值 →
  // 有 diffData 即放行渲染，attr 级 deleted diff 走 RoundDiffCell 展示历史轮内容
  const hasRowDiff = !!diffData?.[row?.group_value ?? ''];

  // 子表单校验实例注册：提交时外层 validateFields 遍历触发；卸载注销（虚拟滚动回收 cell 时自动清理）
  const cellApi = useMemo(
    () => ({ validateFields: () => form.validateFields() }),
    [form]
  );
  useEffect(() => {
    if (!editable || !enabled) return;
    registerCell(row.group_value, cellApi);
    return () => registerCell(row.group_value, null);
  }, [editable, enabled, row.group_value, registerCell, cellApi]);

  // 受控值 → form store 同步（row 变化灌入；同值不触发 onValuesChange，无循环）
  useEffect(() => {
    if (!editable || !enabled) return;
    const values: Record<string, any> = {};
    for (const attr of row.attributes ?? []) {
      values[attr.attr_code] = row[attr.attr_code];
    }
    form.setFieldsValue(values);
  }, [row, editable, enabled, form]);

  // diff 对比态被删除权益行 enabled=false，放行渲染以展示历史值；非 diff 场景行为不变
  if (!enabled && !hasRowDiff) return null;
  const attrs = row.attributes ?? [];
  if (!attrs.length) return null;

  const items = attrs.map((attr) => {
    // 控件配置 + 校验规则同源：均复用 attrToSFormProps（getRequiredRule 联动）
    const { componentClass, elementProps, rules, valueFormat } =
      attrToSFormProps(attr, formValues);
    if (!editable) {
      return {
        label: attr.attr_name,
        // 历史轮次对比态：attr 渲染改走 RoundDiffCell（最新值着色 + 历史值删除线）；非对比态走纯文本
        children: renderBenefitAttrDiff(row, attr, diffData),
      };
    }
    // 编辑态：走 DescriptionsFormItem 的 SFormItem 分支（不传 children）——
    // 控件/校验/错误展示统一由 SFormItem+SFormElement 承担；必填星号由 required-text 自动加
    return {
      label: attr.attr_name,
      formItemKey: attr.attr_code,
      componentClass,
      elementProps,
      rules,
      valueFormat,
    };
  });

  if (!editable) {
    return (
      <DescriptionsFormItem
        className={'ip-cooperation-benefit-config'}
        column={1}
        items={items}
      />
    );
  }

  return (
    <Form
      form={form}
      onValuesChange={(changed: Record<string, any>) => {
        if (Object.keys(changed).length) onRowChange(row, changed);
      }}
    >
      <DescriptionsFormItem
        className={'ip-cooperation-benefit-config'}
        column={1}
        items={items}
      />
    </Form>
  );
}

function BenefitConfigNew(
  {
    options = [],
    value,
    onChange,
    view = false,
    formData = {},
    negotiation,
    mode,
    diffData,
    compareEnabled,
  }: BenefitConfigNewProps,
  ref: React.Ref<BenefitConfigNewRef>
) {
  // 受控 value：父组件未传时用 options 构造初始值
  const controlledValue = value ?? buildInitialValue(options);
  // 对比态开关门控：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；activeDiffData 供展示过滤/行级与 attr 级 diff/谈判列读取
  const activeDiffData = compareEnabled ? diffData : undefined;

  // 同步 ref：onChange 回调中需要拿到最新的 controlledValue（含 group_name/required/attributes 等非编辑字段）
  const valueRef = useRef<BenefitRowData[]>(controlledValue);
  valueRef.current = controlledValue;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // 各权益行 cell 子表单校验实例收集（rowKey=group_value；经 editRender props 传入的 registerCell 注册）。
  // 注意：ProTable cell 渲染链（EditWrapper→cellEditMemo）是 memo 非 forwardRef，会丢弃 ref，故必须用 props 回调注册
  const cellFormMap = useRef<Map<string, BenefitDescCellRef>>(new Map());
  const registerCell = useMemoizedFn(
    (rowKey: string, api: BenefitDescCellRef | null) => {
      if (api) cellFormMap.current.set(rowKey, api);
      else cellFormMap.current.delete(rowKey);
    }
  );

  // 暴露校验能力：遍历各行 cell 子表单校验（列 rule 已下沉到子表单，不再走 ProTable validate）。
  // reject 字符串（聚合各字段错误消息），供父表单 watchChildFormValidate 的 async-validator 取 message
  useImperativeHandle(
    ref,
    () => ({
      validateFields: () => {
        const apis = [...cellFormMap.current.values()];
        if (!apis.length) return Promise.resolve();
        return Promise.all(
          apis.map((api) =>
            Promise.resolve(api.validateFields()).then(
              () => null,
              (err: any) => err
            )
          )
        ).then((results) => {
          const errors = results.filter(Boolean);
          if (!errors.length) return undefined;
          const msgs: string[] = [];
          for (const e of errors) {
            for (const field of e?.errorFields ?? []) {
              msgs.push(...(field?.errors ?? []));
            }
          }
          return Promise.reject(msgs.join('；'));
        });
      },
    }),
    []
  );

  // 条件联动取值：父级 formData + value 中 attr 值合并（表内实时值已由 onRowChange 写回行数据）
  const liveFormValues = useMemo(
    () => ({
      ...formData,
      ...(value ?? []).reduce<Record<string, any>>((acc, r) => {
        for (const attr of r.attributes ?? []) {
          if (r[attr.attr_code] !== undefined) {
            acc[attr.attr_code] = r[attr.attr_code];
          }
        }
        return acc;
      }, {}),
    }),
    [formData, value]
  );

  // 只读态展示行：未开启的权益行（非必填且未勾选 enabled=false）整行不展示。
  // 仅过滤渲染，不改写受控 value/onChange（只读态无编辑/提交）
  const displayValue = useMemo(() => {
    // offer 谈判模式：编辑态也隐藏未开启行（对齐只读态过滤），仅展示 enabled 行
    if (!view && mode !== 'offer-negotiation') return controlledValue;
    // 对比态：带 diff 的行（新增/删除/字段变化）恒展示（含未开启的历史轮删除行），其余仍按开启过滤
    if (activeDiffData) {
      return controlledValue.filter(
        (row) =>
          row?.required ||
          row?.enabled ||
          activeDiffData?.[row?.group_value ?? '']
      );
    }
    return controlledValue.filter((row) => row?.required || row?.enabled);
  }, [view, mode, controlledValue, diffData, compareEnabled]);

  // ProTablePlusForm.onChange 回调：base rows 兜底合并（防内部数据丢失非编辑字段），并同步 valueRef
  const handleFormChange = useCallback((rows: unknown[]) => {
    const baseRows = valueRef.current;
    const merged = (rows as any[]).map((formRow, idx) => ({
      ...(baseRows[idx] ?? {}),
      ...formRow,
    })) as BenefitRowData[];
    valueRef.current = merged;
    onChangeRef.current?.(merged);
  }, []);

  // 行级更新：整行 patch 写回（ProTablePlusForm 受控 rows 范式）
  // 同步更新 valueRef.current，保证连续操作基于最新行（对齐 handleFormChange 行为）
  const handleRowChange = useMemoizedFn(
    (row: BenefitRowData, patch: Partial<BenefitRowData>) => {
      const next = valueRef.current.map((r) =>
        r.group_value === row.group_value ? { ...r, ...patch } : r
      );
      valueRef.current = next;
      onChangeRef.current?.(next);
    }
  );

  // offer 谈判模式删除行：按 group_value 过滤移除（仅可选行渲染删除按钮，必填行不删）
  const handleRemoveBenefit = useMemoizedFn((row: BenefitRowData) => {
    const next = valueRef.current.filter(
      (r) => r.group_value !== row.group_value
    );
    valueRef.current = next;
    onChangeRef.current?.(next);
  });

  // offer 谈判模式新增权益：行数据已是全量权益列表（buildRowsFromBenefits 含 enabled=false 未开启行），
  // 不 push 新行——把 codes 命中的已有行 enabled=true + template 刷新为 options 最新（新增配置用最新定义）
  const handleAddBenefits = useMemoizedFn((codes: string[]) => {
    if (!codes?.length) return;
    const next = enableBenefitRows(valueRef.current, codes, options);
    valueRef.current = next;
    onChangeRef.current?.(next);
  });

  // offer 谈判模式新增下拉选项：可选且未开启的权益分组（必填组恒开启，不进下拉）
  const unEnabledOptions = useMemo(() => {
    if (mode !== 'offer-negotiation') return [];
    const enabledCodes = new Set(
      controlledValue
        .filter((r) => r.required_mode === 1 || r.enabled)
        .map((r) => r.group_value)
    );
    return options.filter(
      (g) => !isBenefitGroupRequired(g) && !enabledCodes.has(g.code)
    );
  }, [mode, options, controlledValue]);

  // 条件必填「不可填」联动清空：required_mode===2 且 required_condition.fallback===3 时，
  // 联动求值 required=false（条件不满足 → fallback=3 不可填）→ 清空该 attr 数据（置 null）。
  // liveFormValues 变化（父级 formData / 表内 attr 编辑）驱动重算；只读态不处理（view 纯展示，不应改写数据）。
  // ProTablePlusForm 由 value 受控驱动内部数据同步，无需像 ProTableForm 手动 setFieldsValue
  useEffect(() => {
    if (view) return;
    const next = clearNotFillableRows(valueRef.current, liveFormValues);
    if (!next) return;
    valueRef.current = next;
    onChangeRef.current?.(next);
  }, [liveFormValues, view]);

  // 谈判列：权益行 group_value 作为 item_key（benefit_code），action/opinion 从 negotiation.items 回显 + editState 编辑。
  // 外包场景传 negotiation（权益内容仍按 view 只读，谈判列独立于 view 用 negotiation.editable 控制）
  const showNegotiation = !!negotiation;
  // 回复/意见仅外包谈判场景可编辑（内包商务修改只读展示版权方回复）——对齐 SolutionTable isExternalEdit 语义
  const negotiationEditable =
    negotiation?.mode === 'external' && (negotiation?.editable ?? false);
  const negotiationItemsByKey = useMemo(() => {
    const map: Record<string, NegotiationItem> = {};
    (negotiation?.items || []).forEach((i) => {
      if (i.item_key) map[i.item_key] = i;
    });
    return map;
  }, [negotiation?.items]);
  const negotiationEditState = negotiation?.editState ?? {};
  // 批量编辑标题（列头 Popover，仅针对本表格权益 code）
  const renderNegotiationBatchTitle = createRenderBatchEditTitle({
    editable: negotiationEditable,
    onBatchAction: negotiation?.onBatchAction,
  });
  // 权益条款 key（批量编辑范围 = 本表格权益 code）
  const benefitItemKeys = useMemo(
    () =>
      Array.from(
        new Set(controlledValue.map((r) => r.group_value).filter(Boolean))
      ),
    [controlledValue]
  );

  if (!options.length) return null;

  const columns: AddGroupFormColumn[] = [
    {
      title: '权益名称',
      dataIndex: 'group_name',
      // 外包内容列总宽统一 700（权益名称 130 + 权益说明 570）；内包保持 120
      width: negotiation?.mode === 'external' ? 130 : `120px`,
      editRender: ({ row }: any) => ({
        name: 'custom-component',
        editable: !view,
        showReadonlyState: true,
        props: {
          customComp: BenefitNameCell,
          editable: !view,
          row: row as BenefitRowData,
          onRowChange: handleRowChange,
          // offer 谈判模式隐藏勾选开关
          hideCheckbox:
            mode === 'offer-negotiation' ||
            negotiation?.mode === 'external' ||
            view,
          // 历史轮次对比态：行级 added/deleted 着色权益名称
          diffData: activeDiffData,
        },
      }),
    },
    {
      title: '权益说明',
      dataIndex: 'desc',
      // 外包内容列总宽统一 700（权益说明 570）；内包保持协作者调整值 490
      width: negotiation?.mode === 'external' ? 570 : view ? `520px` : 490,
      onCell: () => ({
        className: 'px-0 py-0',
        // 外包走查：权益说明单元格需与右侧「回复」操作区有分割线；左侧线已有，补右侧线形成边界
      }),
      editRender: ({ row }: any) => ({
        name: 'custom-component',
        editable: !view,
        showReadonlyState: true,
        props: {
          customComp: BenefitDescCell,
          editable: !view,
          row: row as BenefitRowData,
          onRowChange: handleRowChange,
          formValues: liveFormValues,
          // cell 子表单校验实例注册（列 rule 已下沉到子表单；提交时外层 validateFields 遍历触发）
          registerCell,
          // 历史轮次对比态：attr 只读渲染改走 RoundDiffCell（最新值着色 + 历史值删除线）
          diffData: activeDiffData,
        },
      }),
    },
  ];

  // offer 谈判模式编辑态：表格末尾追加「操作」列（删除按钮，必填行不渲染）
  if (mode === 'offer-negotiation' && !view) {
    columns.push({
      title: '操作',
      dataIndex: 'operation',
      width: 60,
      render: (_: any, row: BenefitRowData) => (
        <Button
          type="link"
          danger
          disabled={row?.required}
          onClick={() => handleRemoveBenefit(row)}
        >
          删除
        </Button>
      ),
    });
  }

  // 谈判列：与权益名称/说明 同层级追加「回复」「意见反馈/说明」（item_key = benefit_code，参考 SolutionTable）
  if (showNegotiation) {
    const replyColumn: AddGroupFormColumn = {
      title: renderNegotiationBatchTitle(benefitItemKeys),
      dataIndex: 'action',
      // 回复列固定宽（走查：编辑态 180px 放三个 Medium Tag / 只读态 100px 放单 Tag；negotiationEditable 即谈判编辑态）
      width: negotiationEditable ? 180 : `100px`,
      // 外包走查 recZIyp：回复列右侧描边缺失（意见反馈列弹性无宽，verticalBorder 不渲染该侧边框）→ 回复列 th/td 加右边框（仅外包模式，内包保持现状）
      ...(negotiation?.mode === 'external'
        ? {
            onCell: () => ({
              style: { borderRight: '1px solid rgb(223, 224, 229)' },
            }),
            onHeaderCell: () => ({
              style: { borderRight: '1px solid rgb(223, 224, 229)' },
            }),
          }
        : {}),
      editRender: ({ row }: any) => ({
        name: 'custom-component',
        editable: negotiationEditable,
        showReadonlyState: true,
        props: {
          customComp: ActionCell,
          row: { ...row, item_key: row.group_value },
          itemsByKey: negotiationItemsByKey,
          editState: negotiationEditState,
          editable: negotiationEditable,
          readOnly: !negotiationEditable,
          onEditChange: negotiation?.onEditChange,
          // 历史轮次对比态：回复 diff（readOnly 时 ActionCell 渲染新旧对比）
          actionDiff: activeDiffData?.[row?.group_value]?.action,
        },
      }),
    };
    const opinionColumn: AddGroupFormColumn = {
      title: getOpinionTitle(),
      dataIndex: 'opinion',
      // 外包：不设固定宽（弹性列吃剩余，最小宽 100 防坍缩，固定列严格按配置渲染 → 回复列 x 对齐）；内包保持固定 400
      ...(negotiation?.mode === 'external' ? { minWidth: 100 } : { width: `400px` }),
      editRender: ({ row }: any) => ({
        name: 'custom-component',
        editable: negotiationEditable,
        showReadonlyState: true,
        props: {
          customComp: OpinionCell,
          row: { ...row, item_key: row.group_value },
          itemsByKey: negotiationItemsByKey,
          editState: negotiationEditState,
          editable: negotiationEditable,
          readOnly: !negotiationEditable,
          onEditChange: negotiation?.onEditChange,
          // 历史轮次对比态：意见反馈 diff（readOnly 时 OpinionCell 渲染新旧对比）
          opinionDiff: activeDiffData?.[row?.group_value]?.opinion,
        },
      }),
    };

    columns.push(replyColumn, opinionColumn);
  }

  // offer 谈判模式编辑态：表格底部 summary 展示「新增权益」按钮（参考 CategoriesRights EquityFormTable renderSummary）
  const renderSummary = () => {
    const colSpan = columns.length + 1;
    return (
      <Table.Summary>
        <Table.Summary.Row style={{ background: '#fff' }}>
          <Table.Summary.Cell index={0} colSpan={colSpan}>
            <Button
              type="checked"
              icon={<Plus />}
              onClick={() => {
                AddBenefitModal.show({
                  options: unEnabledOptions,
                  onOk: (codes) => handleAddBenefits(codes),
                });
              }}
            >
              新增权益
            </Button>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <ProTablePlusForm<BenefitRowData>
      columns={columns}
      value={displayValue}
      onChange={handleFormChange}
      rowKey="group_value"
      size="small"
      scroll={{ x: negotiation?.mode === 'external' ? '100%' : 'max-content' }}
      pagination={false}
      sortable={false}
      verticalBorder={true}
      // offer 谈判模式编辑态：表格底部「新增权益」summary
      summary={
        mode === 'offer-negotiation' && !view ? renderSummary : undefined
      }
      // 有谈判列时不用 ProTable 全局 readOnly（谈判列需可编辑），权益列只读靠 cell 级 editable=!view
      readOnly={view && !showNegotiation}
    />
  );
}

BenefitConfigNew.displayName = 'BenefitConfigNew';

export default forwardRef<BenefitConfigNewRef, BenefitConfigNewProps>(
  BenefitConfigNew
);
